import { useState } from 'react';
import { useTheme } from '../lib/theme-provider';
import { ThemedCard } from './ThemedCard';
import { ThemedButton } from './ThemedButton';
import { Lock, X, Eye, EyeOff } from 'lucide-react';

interface PasswordInputModalProps {
  isOpen: boolean;
  roomCode: string;
  onClose: () => void;
  onConfirm: (password: string) => void;
}

export default function PasswordInputModal({
  isOpen,
  roomCode,
  onClose,
  onConfirm,
}: PasswordInputModalProps) {
  const { theme } = useTheme();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (password.length < 4) {
      setError('密码至少需要4位数字');
      return;
    }
    setError('');
    onConfirm(password);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-24"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }}
    >
      <ThemedCard className="w-full max-w-sm p-6 mb-16">
        {/* Header */}
        <div className="flex items-center justify-center mb-4">
          <div
            className="flex items-center gap-2"
            style={{ color: theme.colors['accent-gold'] }}
          >
            <Lock size={24} />
            <h2
              className="text-xl font-bold"
              style={{ color: theme.colors['text-primary'] }}
            >
              输入密码
            </h2>
          </div>
        </div>

        {/* 房间信息 */}
        <div
          className="mb-4 p-3 rounded-lg text-center"
          style={{
            backgroundColor: theme.colors['bg-primary'],
            color: theme.colors['text-secondary'],
          }}
        >
          <span className="text-sm">房间号: </span>
          <span
            className="text-lg font-bold"
            style={{ color: theme.colors['accent-gold'] }}
          >
            {roomCode}
          </span>
        </div>

        {/* 密码输入 */}
        <div className="mb-4">
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value.replace(/[^0-9]/g, '').slice(0, 6));
                setError('');
              }}
              onKeyDown={handleKeyDown}
              placeholder="请输入4-6位数字密码"
              maxLength={6}
              className="w-full px-4 py-3 pr-12 rounded-xl text-center text-lg tracking-widest"
              style={{
                backgroundColor: theme.colors['bg-primary'],
                border: `1px solid ${
                  error ? theme.colors['accent-red'] : theme.colors['border-secondary']
                }`,
                color: theme.colors['text-primary'],
              }}
            />
            <button
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1"
            >
              {showPassword ? (
                <EyeOff size={20} style={{ color: theme.colors['text-muted'] }} />
              ) : (
                <Eye size={20} style={{ color: theme.colors['text-muted'] }} />
              )}
            </button>
          </div>

          {/* 错误提示 */}
          {error && (
            <p
              className="mt-2 text-sm text-center"
              style={{ color: theme.colors['accent-red'] }}
            >
              {error}
            </p>
          )}
        </div>

        {/* 数字键盘 */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              key={num}
              onClick={() => {
                if (password.length < 6) {
                  setPassword(password + num);
                  setError('');
                }
              }}
              className="py-4 rounded-xl text-xl font-bold transition-colors"
              style={{
                backgroundColor: theme.colors['bg-primary'],
                border: `1px solid ${theme.colors['border-secondary']}`,
                color: theme.colors['text-primary'],
              }}
            >
              {num}
            </button>
          ))}
          <button
            onClick={() => setPassword(password.slice(0, -1))}
            className="py-4 rounded-xl text-sm font-medium transition-colors"
            style={{
              backgroundColor: theme.colors['bg-primary'],
              border: `1px solid ${theme.colors['border-secondary']}`,
              color: theme.colors['text-secondary'],
            }}
          >
            删除
          </button>
          <button
            onClick={() => {
              if (password.length < 6) {
                setPassword(password + '0');
                setError('');
              }
            }}
            className="py-4 rounded-xl text-xl font-bold transition-colors"
            style={{
              backgroundColor: theme.colors['bg-primary'],
              border: `1px solid ${theme.colors['border-secondary']}`,
              color: theme.colors['text-primary'],
            }}
          >
            0
          </button>
          <button
            onClick={() => setPassword('')}
            className="py-4 rounded-xl text-sm font-medium transition-colors"
            style={{
              backgroundColor: theme.colors['bg-primary'],
              border: `1px solid ${theme.colors['border-secondary']}`,
              color: theme.colors['text-secondary'],
            }}
          >
            清空
          </button>
        </div>

        {/* 操作按钮 */}
        <div className="flex gap-3">
          <ThemedButton variant="secondary" onClick={onClose} className="flex-1">
            <X size={18} className="mr-1" />
            取消
          </ThemedButton>
          <ThemedButton
            onClick={handleConfirm}
            disabled={password.length < 4}
            className="flex-1"
          >
            确认
          </ThemedButton>
        </div>
      </ThemedCard>
    </div>
  );
}
